import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AccessTime as TimeIcon, ArrowBack, ChevronLeft, Close } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers';
import { Alert } from '../popupAlerts';
import dayjs from 'dayjs';
import LoadingDial from '../loading/LoadingDial';
import {
  Box,
  Button,
  ButtonBase,
  ButtonGroup,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { alpha } from '@mui/material/styles';

import { buildCron, parserCron, initCron, timezone } from '@hotglue/common';

import { getSchedule, updateSchedule, deleteSchedule } from '../../api/client';
import { getListener, getSelectedFlow } from '../../redux/selectors';

const FREQUENCIES = ['Hourly', 'Daily', 'Weekly', 'Monthly'];
const INTERVALS = ['Hours', 'Minutes'];

const DAYS_OF_WEEK = [
  { label: 'S', value: '0' },
  { label: 'M', value: '1' },
  { label: 'T', value: '2' },
  { label: 'W', value: '3' },
  { label: 'T', value: '4' },
  { label: 'F', value: '5' },
  { label: 'S', value: '6' },
];

const DAYS_OF_MONTH = [...Array(31).keys()].map((a) => a + 1);

const LinkedSchedule = ({ flow, entity }) => {
  const [frequency, setFrequency] = useState('');
  const [minuteOfTheHour, setMinuteOfTheHour] = useState(null);
  const [timeOfTheDay, setTimeOfTheDay] = useState(dayjs(new Date()));
  const [dayOfTheWeek, setDayOfTheWeek] = useState([]);
  const [dayOfTheMonth, setDayOfTheMonth] = useState('1st');
  const [everyMinute, setEveryMinute] = useState(null);
  const [everyHour, setEveryHour] = useState(null);
  const [type, setType] = useState('fixed');
  const [interval, setInterval] = useState('Hours');
  const [enabled, setEnabled] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const isIntervalEnabled = useSelector((state) => state.preferences.isIntervalEnabled);
  const isConnectorScheduleEnabled = useSelector(state => state.preferences.isConnectorSchedule)
  const entityId = useMemo(() => (flow.type ? entity.target : entity.id ? entity.id : entity.tap), [entity, flow]);

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const response = await getSchedule(undefined, entity.tenant_id, isConnectorScheduleEnabled ? entityId : undefined);
        const { ScheduleExpression, State } = response;
        let cron;
        if (State === 'DISABLED') {
          cron = initCron();
          setEnabled(false);
        } else {
          const matchExpr = ScheduleExpression && ScheduleExpression.match(/^(cron\()?([^)]*)\)?/);
          const scheduleExpression = matchExpr && matchExpr.length > 0 && matchExpr[2];
          cron = parserCron(scheduleExpression);
          setEnabled(true);
        }
        setInterval(cron.interval);
        if (cron.everyHour) setEveryHour(dayjs(new Date()).set('hours', cron.everyHour));
        if (cron.everyMinute) setEveryMinute(dayjs(new Date()).set('minutes', cron.everyMinute));
        setType(isIntervalEnabled ? cron.type : 'fixed');
        setFrequency(cron.frequency);
        setTimeOfTheDay(dayjs(cron.timeOfTheDay || new Date()));
        setDayOfTheWeek(cron.dayOfTheWeek);
        setDayOfTheMonth(cron.dayOfTheMonth);
        setMinuteOfTheHour(dayjs(cron.minuteOfTheHour || new Date()));
        setSchedule(ScheduleExpression);
        setLoading(false);
      } catch (err) {
        Alert({ type: 'error', text: `Failed to load the schedule: ${err}` });
        throw err;
      }
    };

    if (!flow) return;

    setLoading(true);
    loadSchedule();
  }, [flow, schedule, entityId]);

  const onSave = async () => {
    if (type === 'fixed' && frequency !== 'Hourly' && timeOfTheDay.toString() === 'Invalid Date') return;
    if (
      type === 'fixed' &&
      frequency === 'Hourly' &&
      (!minuteOfTheHour || minuteOfTheHour?.toString() === 'Invalid Date')
    ) {
      Alert({ type: 'error', text: `the minute passed is invalid` });
      return;
    }
    if (type === 'fixed' && timeOfTheDay.toString() === 'Invalid Date') return;
    if (type === 'interval' && !everyHour && !everyMinute && isIntervalEnabled) {
      Alert({ type: 'error', text: `Please enter a frequency` });
      return;
    }
    if (
      type === 'interval' &&
      interval === 'Hours' &&
      isIntervalEnabled &&
      (!everyHour ||
        everyHour.toString() === 'Invalid Date' ||
        parseInt(dayjs(everyHour).format('HH')) > 23 ||
        parseInt(dayjs(everyHour).format('HH')) < 1)
    ) {
      Alert({ type: 'error', text: `Please enter a valid hour frequency between 1 - 23` });
      return;
    }
    if (
      type === 'interval' &&
      interval === 'Minutes' &&
      isIntervalEnabled &&
      (!everyMinute ||
        everyMinute.toString() === 'Invalid Date' ||
        parseInt(dayjs(everyMinute).format('mm')) > 59 ||
        parseInt(dayjs(everyMinute).format('mm')) < 1)
    ) {
      Alert({ type: 'error', text: `Please enter a valid minute frequency between 1 - 59` });
      return;
    }
    const scheduleExpr = buildCron({
      type,
      interval,
      everyHour: dayjs(everyHour).format('H'),
      everyMinute: dayjs(everyMinute).format('m'),
      frequency,
      minuteOfTheHour: minuteOfTheHour?.toDate(),
      timeOfTheDay: timeOfTheDay.toDate(),
      dayOfTheWeek,
      dayOfTheMonth,
    });

    try {
      const oldSchedule = schedule;
      const scheduleData = {
        state: 'ENABLED',
        schedule_expression: scheduleExpr,
      }

      if(isConnectorScheduleEnabled)
        scheduleData.connector_id = entityId

      await updateSchedule(
        scheduleData,
        undefined,
        entity.tenant_id,
      );

      Alert({ type: 'success', text: `Your sync schedule was updated.` });
      setSchedule(scheduleExpr);

      // Call the onScheduleSave listener if it exists
      const listener = getListener();
      const flowId = getSelectedFlow();
      if (listener && listener.onScheduleSave) {
        listener.onScheduleSave(
          oldSchedule || null,
          scheduleExpr,
          entityId,
          flowId,
          entity.tenant_id
        );
      }
    } catch (err) {
      Alert({ type: 'error', text: `Failed to save the schedule: ${err}` });
    }
  };

  const onClear = async () => {
    try {
      await deleteSchedule(undefined, entity.tenant_id, entityId);

      setEnabled(false);
      let cron = initCron();
      setFrequency(cron.frequency);
      setTimeOfTheDay(dayjs(cron.timeOfTheDay));
      setDayOfTheWeek(cron.dayOfTheWeek);
      setDayOfTheMonth(cron.dayOfTheMonth);
      setMinuteOfTheHour(dayjs(cron.minuteOfTheHour));
      setType('fixed');
      setEveryHour(null);
      setEveryMinute(null);
      setInterval('Hours');
      Alert({ type: 'success', text: `Sync schedule cleared.` });
      const listener = getListener();
      const flowId = getSelectedFlow();
      if (listener && listener.onScheduleSave) {
        listener.onScheduleSave(
          schedule || null,
          null,
          entityId,
          flowId,
          entity.tenant_id
        );
      }
    } catch (err) {
      Alert({ type: 'error', text: `Failed to clear the schedule: ${err}` });
      console.error(err, err.stack);
    }
  };
  const toggleButton = (weekDay) => {
    let nWeekDays = dayOfTheWeek.slice(0);
    if (!dayOfTheWeek.find((w) => w == weekDay)) nWeekDays.push(weekDay);
    else nWeekDays = nWeekDays.filter((w) => w !== weekDay);

    setDayOfTheWeek(nWeekDays);
  };

  if (isLoading) return <LoadingDial />;

  return (
    <Box display="flex" flexDirection="column" width="100%" flex={1}>
      <Box display="flex" flexDirection="column" width="100%" gap={4}>
        <Box display="flex" flexDirection="column" gap={1} width="100%">
          <Box>
            <Typography variant="body1">Edit Sync Schedule</Typography>
            <Typography variant="body2" color="text.lightish">
              Set a sync schedule to automatically trigger jobs.
            </Typography>
          </Box>
          {isIntervalEnabled && (
            <Box display="flex" height={'40px'} mt={'16px'}>
              <Box
                component={ButtonBase}
                display="flex"
                variant="outlined"
                onClick={() => setType('fixed')}
                color="primary"
                flex={1}
                alignItems="center"
                justifyContent="center"
                sx={{
                  cursor: 'pointer',
                  borderTopLeftRadius: '4px',
                  borderBottomLeftRadius: '4px',
                  border: ({ palette }) => `1px solid ${palette.divider}`,
                  ...(type === 'fixed' && {
                    border: ({ palette }) => `1px solid ${palette.primary.main}`,
                    backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.3),
                    p: {
                      color: ({ palette }) => palette.primary.main,
                    },
                  }),
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: ({ palette }) => (type === 'fixed' ? palette.primary.main : palette.text.primary),
                  }}
                >
                  Fixed
                </Typography>
              </Box>
              <Box
                display="flex"
                component={ButtonBase}
                variant="outlined"
                onClick={() => setType('interval')}
                flex={1}
                alignItems="center"
                justifyContent="center"
                sx={{
                  cursor: 'pointer',
                  borderTopRightRadius: '4px',
                  borderBottomRightRadius: '4px',
                  border: ({ palette }) => `1px solid ${palette.divider}`,
                  ...(type === 'interval' && {
                    border: ({ palette }) => `1px solid ${palette.primary.main}`,
                    backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.3),
                    p: {
                      color: ({ palette }) => palette.primary.main,
                    },
                  }),
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: ({ palette }) => (type === 'interval' ? palette.primary.main : palette.text.primary),
                  }}
                >
                  Interval
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {type === 'fixed' && (
          <>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography component="label" variant="body2">
                Frequency
              </Typography>
              <Select
                fullWidth
                role='combobox'
                title={'frequency combobox'}
                aria-label={'frequency combobox'}
                labelId="frequency-label-id"
                label={'Frequency'}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                variant="standard"
                size="medium"
              >
                {FREQUENCIES.map((frequency) => (
                  <MenuItem key={`f-${frequency}`} value={frequency}>
                    {frequency}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {frequency === 'Weekly' && (
              <Box display="flex" flexDirection="column" gap={0.5}>
                <ButtonGroup variant="outlined" color="primary" size="small" sx={{ flexWrap: 'wrap', gap: 1.1875 }}>
                  {DAYS_OF_WEEK.map((dow) => (
                    <Button
                      key={dow.value}
                      sx={{
                        backgroundColor: ({ palette }) =>
                          dayOfTheWeek?.find((w) => w == dow.value) ? palette.primary.main : palette.background.paper,
                        color: ({ palette }) =>
                          dayOfTheWeek?.find((w) => w == dow.value) ? palette.common.white : palette.primary.main,
                        minWidth: '40px',
                      }}
                      onClick={() => toggleButton(dow.value)}
                    >
                      {dow.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            )}
            {frequency === 'Monthly' && (
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography component="label" variant="body2">
                  Day of the month
                </Typography>
                <Select
                  fullWidth
                  labelId="day-of-month-id"
                  role='combobox'
                  aria-label="day of the month combobox"
                  id="demo-simple-select-outlined"
                  size="medium"
                  value={dayOfTheMonth}
                  onChange={(e) => setDayOfTheMonth(e.target.value)}
                  label={'Day of the month'}
                  variant="standard"
                  MenuProps={{
                    style: {
                      maxHeight: 240,
                    },
                  }}
                >
                  {DAYS_OF_MONTH.map((_day) => (
                    <MenuItem key={`d-${_day}`} value={_day}>
                      {_day}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            {frequency === 'Hourly' && (
              <TimePicker
                id="time-picker"
                ampm={false}
                inputVariant={'outlined'}
                label="Select the minute of the hour"
                views={['minutes']}
                value={minuteOfTheHour}
                inputFormat="mm"
                fullWidth
                onChange={(val) => {
                  setMinuteOfTheHour(val);
                }}
                slotProps={{
                  layout : {
                    sx: {
                      display: 'initial'
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& input': {
                        p: '5px 0 10px',
                      },
                      '& button': {
                        mb: '10px',
                        mr: '0px',
                      },
                    }}
                    {...params}
                  />
                )}
              />
            )}
            {frequency !== 'Hourly' && (
              <TimePicker
                id="time-picker"
                inputVariant={'outlined'}
                label="Time of the day"
                value={timeOfTheDay}
                fullWidth
                onChange={(val) => {
                  setTimeOfTheDay(val);
                }}
                slotProps={{
                  layout : {
                    sx: {
                      display: 'initial'
                    }
                  },
                  textField: { InputLabelProps: { shrink: true }}
                }}

                // keyboardIcon={<TimeIcon fontSize="small" />}
                KeyboardButtonProps={{
                  'aria-label': 'change time',
                }}
                renderInput={(params) => (
                  <TextField
                    variant="standard"
                    {...params}
                    sx={{
                      '& input': {
                        p: '5px 0 10px',
                      },
                      '& button': {
                        mb: '10px',
                        mr: '0px',
                      },
                    }}
                  />
                )}
              />
            )}
          </>
        )}
        {type === 'interval' && (
          <Box display="flex" flexDirection={'column'}>
            <Box
              display="flex"
              gap={2}
              sx={{
                '&> div:first-child': {
                  mt: '2px',
                },
              }}
            >
              {interval === 'Hours' && (
                <TimePicker
                  sx={{
                    mt: '2px',
                  }}
                  id="time-picker"
                  ampm={false}
                  inputVariant={'outlined'}
                  label="Frequency"
                  views={['hours']}
                  value={everyHour}
                  slotProps={{
                    layout : {
                      sx: {
                        display: 'initial'
                      }
                    },
                    textField: { InputLabelProps: { shrink: true }}
                  }}
                  inputFormat="H"
                  ampmInClock={false}
                  shouldRespectLeadingZeros={false}
                  fullWidth
                  onChange={(val) => {
                    setEveryHour(val);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      fullWidth
                      sx={{
                        flex: 1,
                        '& input': {
                          p: '5px 0 10px',
                        },
                        '& button': {
                          mb: '10px',
                          mr: '0px',
                        },
                      }}
                    />
                  )}
                />
              )}
              {interval === 'Minutes' && (
                <TimePicker
                  id="time-picker"
                  sx={{
                    mt: '2px',
                  }}
                  ampm={false}
                  inputVariant={'outlined'}
                  label="Frequency"
                  views={['minutes']}
                  value={everyMinute}
                  inputFormat="m"
                  fullWidth
                  shouldRespectLeadingZeros={false}
                  slotProps={{
                    layout : {
                      sx: {
                        display: 'initial'
                      }
                    },
                    textField: { InputLabelProps: { shrink: true }}
                  }}
                  onChange={(val) => {
                    setEveryMinute(val);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      sx={{
                        flex: 1,
                        '& input': {
                          p: '5px 0 10px',
                        },
                        '& button': {
                          mb: '10px',
                          mr: '0px',
                        },
                      }}
                    />
                  )}
                />
              )}
              <Box display="flex" flexDirection="column" gap={0.5} width={'100%'} flex={1}>
                <Typography component="label" variant="body2">
                  Interval
                </Typography>
                <Select
                  fullWidth
                  aria-label={'interval combobox'}
                  role='combobox'
                  labelId="interval-label-id"
                  label={'interval'}
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                  variant="standard"
                  size="medium"
                >
                  {INTERVALS.map((interval) => (
                    <MenuItem key={`f-${interval}`} value={interval}>
                      {interval}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 1,
              }}
            >
              {interval === 'Hours' && everyHour ? (
                dayjs(everyHour).format('HH') === 'Invalid Date' ||
                parseInt(dayjs(everyHour).format('HH')) > 23 ||
                parseInt(dayjs(everyHour).format('HH')) < 1 ? (
                  <Typography variant="body2" color="error.main">
                    Insert a valid hour frequency between 1 and 23
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.lightish">
                    {`Jobs will run every ${dayjs(everyHour).format('H')} ${
                      dayjs(everyHour).format('HH') === '01'
                        ? interval.toLowerCase().substring(0, interval.length - 1)
                        : interval.toLowerCase()
                    }`}
                  </Typography>
                )
              ) : (
                interval === 'Minutes' &&
                everyMinute &&
                (dayjs(everyMinute).format('mm') === 'Invalid Date' ||
                parseInt(dayjs(everyMinute).format('mm')) > 59 ||
                parseInt(dayjs(everyMinute).format('mm')) < 1 ? (
                  <Typography variant="body2" color="error.main">
                    Insert a valid minute frequency between 1 and 59
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.lightish">
                    {`Jobs will run every ${dayjs(everyMinute).format('m')} ${
                      dayjs(everyMinute).format('mm') === '01'
                        ? interval.toLowerCase().substring(0, interval.length - 1)
                        : interval.toLowerCase()
                    }`}
                  </Typography>
                ))
              )}
            </Box>
          </Box>
        )}
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          py={1}
          sx={{ borderBottom: ({ palette }) => `1px solid ${palette.common.divider}` }}
        >
          <Typography variant="body2">Status</Typography>
          <Chip
            variant="outlined"
            label={
              <Typography variant="body1" color={enabled ? 'success' : 'default'}>
                {enabled ? 'Enabled' : 'Disabled'}
              </Typography>
            }
            size="small"
            color={enabled ? 'success' : 'default'}
          />
        </Box>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          py={1}
          mb={2}
          sx={{ borderBottom: ({ palette }) => `1px solid ${palette.common.divider}` }}
        >
          <Typography variant="body2">Timezone</Typography>
          <Typography variant="body1" color="text.lightish">
            {timezone}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" flex={1} py={2} justifyContent="space-between" alignItems="flex-end" mb={2}>
        <Button variant="outlined" color="text" sx={{ px: 3 }} onClick={onClear}>
          Clear
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!timeOfTheDay || timeOfTheDay.toString() === 'Invalid Date'}
          onClick={onSave}
          sx={{ px: 4 }}
        >
          {'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default LinkedSchedule;
